import { eq, inArray } from "@tanstack/db";
import type { AppRouterContext } from "~/lib/router-runtime";
import { getAssetDomainCollections } from "./data/db/collection-registry";
import { normalizeAssetDomainAssetIds } from "./data/db/models";

/** Settle the public asset sources before a background render can finish.
 * These are the same structured queries consumed by useAssetDomainSlice. */
export async function preloadPublicAssetRows(
  context: AppRouterContext,
  assetIds: readonly string[],
): Promise<void> {
  const ids = normalizeAssetDomainAssetIds(assetIds);
  if (ids.length === 0) return;
  const collections = getAssetDomainCollections({
    sessionScopeToken: context.auth.sessionQueryScope.scopeToken,
  });
  await Promise.all([
    context.dbClient.preloadLiveQuery({
      query: (q) =>
        ids.length === 1
          ? q
              .from({ asset: collections.asset })
              .where(({ asset }) => eq(asset.assetId, ids[0]!))
          : q
              .from({ asset: collections.asset })
              .where(({ asset }) => inArray(asset.assetId, ids)),
    }),
    context.dbClient.preloadLiveQuery({
      query: (q) =>
        ids.length === 1
          ? q
              .from({ assetPrice: collections.assetPrice })
              .where(({ assetPrice }) => eq(assetPrice.assetId, ids[0]!))
          : q
              .from({ assetPrice: collections.assetPrice })
              .where(({ assetPrice }) => inArray(assetPrice.assetId, ids)),
    }),
  ]);
}

/** The same structured query as useAssetUniverseCanonicalSlice. Preloading
 * records an ordered DB result snapshot in addition to explicit source rows. */
export async function preloadPublicCanonicalRows(
  context: AppRouterContext,
  assetCanonicalIds: readonly string[],
): Promise<void> {
  const ids = [
    ...new Set(assetCanonicalIds.map((id) => id.trim()).filter(Boolean)),
  ].sort((left, right) => left.localeCompare(right));
  if (ids.length === 0) return;
  const { assetUniverseCanonical } = getAssetDomainCollections({
    sessionScopeToken: context.auth.sessionQueryScope.scopeToken,
  });
  await context.dbClient.preloadLiveQuery({
    query: (q) =>
      ids.length === 1
        ? q
            .from({ assetUniverseCanonical })
            .where(({ assetUniverseCanonical: row }) =>
              eq(row.assetCanonicalId, ids[0]!),
            )
        : q
            .from({ assetUniverseCanonical })
            .where(({ assetUniverseCanonical: row }) =>
              inArray(row.assetCanonicalId, ids),
            ),
  });
}
